import styled from "@xstyled/styled-components";
import { useMemo, useReducer } from "react";
import { useQuery } from "@apollo/client";

import { useJobEditFormikContext } from "../../useHandlers";
import { AddButton } from "../AddButton";
import { UKCountryName } from "../../util";

import { FieldWrapper, SelectField, Spacing, Text, Tipbox } from "@otta/design";
import { JobStructuredLocationsDocument } from "@hire/schema";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: sm;
  max-width: 400;
`;

export function StructuredOfficeLocation({
  matchingLocations,
}: {
  matchingLocations?: string;
}) {
  const form = useJobEditFormikContext();
  const [showField, onShowField] = useReducer(
    () => true,
    form.values.visibleStructuredLocationPreferences.length > 0
  );
  const { data } = useQuery(JobStructuredLocationsDocument, {
    variables: {
      country: UKCountryName,
    },
    skip: !showField,
  });

  const options = useMemo(() => {
    if (!data?.jobStructuredLocations) {
      return [];
    }

    const groups: Record<string, { label: string; value: string }[]> = {};

    for (const option of data?.jobStructuredLocations || []) {
      const area = option.countryArea.area;
      const name = option.name;

      if (name && area) {
        const countyList = groups[area] || [];
        countyList.push({ label: name, value: option.id });
        groups[area] = countyList;
      }
    }

    return Object.entries(groups).map(([label, options]) => ({
      label,
      options,
    }));
  }, [data]);

  const value = useMemo(
    () =>
      options.flatMap(group =>
        group.options.filter(o =>
          form.values.visibleStructuredLocationPreferences.includes(o.value)
        )
      ),
    [form.values.visibleStructuredLocationPreferences, options]
  );

  if (!showField) {
    return (
      <Wrapper>
        <AddButton onClick={onShowField} isOptional>
          Add a different UK location
        </AddButton>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <FieldWrapper
        showOptionalTag={false}
        label="Other in-office locations"
        advice="Additional locations, used for display only."
        fieldError={
          form.touched.visibleStructuredLocationPreferences
            ? form.errors.visibleStructuredLocationPreferences
            : undefined
        }
      >
        {({ field }) => (
          <SelectField
            inputId={field.id}
            placeholder="Type or select locations&hellip;"
            options={options}
            value={value}
            onChange={selected => {
              const newValue = selected
                ? Array.isArray(selected)
                  ? selected.map(v => v.value)
                  : [selected]
                : [];

              form.setFieldValue(
                "visibleStructuredLocationPreferences",
                newValue
              );
            }}
            onBlur={() =>
              form.setFieldTouched(
                "visibleStructuredLocationPreferences",
                true,
                !!form.validateOnBlur
              )
            }
            aria-describedby={field["aria-describedby"]}
            aria-invalid={field["aria-invalid"]}
            hideSelectedOptions={false}
            closeMenuOnSelect={false}
            isMulti
          />
        )}
      </FieldWrapper>
      <Tipbox level="information">
        <Spacing>
          <Text bold>Not all locations support matching</Text>
          <Text>
            We're working on bringing matching to jobs and candidates in other
            locations.
          </Text>
          {matchingLocations && (
            <Text>
              For now, this job will be shown on your company profile, and to
              candidates looking in {matchingLocations}
            </Text>
          )}
        </Spacing>
      </Tipbox>
    </Wrapper>
  );
}
